import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import TournamentCard from './TournamentCard';
import TeamTabEmptyState from './TeamTabEmptyState';
import SearchTournamentModal from './SearchTournamentModal';
import SelectDivisionGroupModal from './SelectDivisionGroupModal';
import { fetchTeamSnapshots, fetchDivisions, joinTournamentAsTeam } from '../api/myTeamApi';
import type { IMyTeam, ITournament, ITournamentSearchResult } from '../types/myTeam';

interface TournamentsTabProps {
  teamId: string;
  teamName: string;
  team: IMyTeam;
  searchModalOpen: boolean;
  onSearchModalOpenChange: (open: boolean) => void;
  onTournamentClick?: (tournament: ITournament) => void;
}

function TournamentsTab({ teamId, team, searchModalOpen, onSearchModalOpenChange, onTournamentClick }: TournamentsTabProps) {
  const [tournaments, setTournaments] = useState<ITournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [divisionModalOpen, setDivisionModalOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<ITournamentSearchResult | null>(null);

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchTeamSnapshots(teamId);
      setTournaments(list);
    } catch (err) {
      console.error('Failed to load tournaments', err);
      toast.error('Failed to load tournaments.');
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  const handleSelectTournament = useCallback(
    async (tournament: ITournamentSearchResult) => {
      onSearchModalOpenChange(false);
      if (!tournament.tournamentId || !teamId) return;
      try {
        const payload = await fetchDivisions(tournament.tournamentId);
        const list =
          Array.isArray(payload)
            ? payload
            : payload?.divisionResponseList ??
              (payload as { divisionResponses?: unknown[] })?.divisionResponses ??
              [];
        const divisions = Array.isArray(list) ? list : [];
        if (divisions.length === 0) {
          await joinTournamentAsTeam(tournament.tournamentId, teamId, undefined);
          toast.success('Team joined tournament successfully.');
          loadTournaments();
        } else {
          let leagueId = tournament.leagueId;
          if (!leagueId && payload && typeof payload === 'object') {
            const p = payload as Record<string, unknown>;
            leagueId = (p.leagueId as string) || undefined;
            if (!leagueId) {
              const divList = (p.divisionResponseList ?? p.divisionResponses) as Array<{
                teams?: Array<{ league?: { leagueId?: string }; leagueId?: string }>;
                childDivisions?: Array<{ teams?: Array<{ league?: { leagueId?: string }; leagueId?: string }> }>;
              }> | undefined;
              const extractLeagueId = (list: typeof divList): string | undefined => {
                if (!list) return undefined;
                for (const div of list) {
                  for (const t of div.teams ?? []) {
                    const id = t?.league?.leagueId ?? (t as { leagueId?: string }).leagueId;
                    if (id) return id;
                  }
                  const fromChild = extractLeagueId(div.childDivisions);
                  if (fromChild) return fromChild;
                }
                return undefined;
              };
              leagueId = extractLeagueId(divList);
            }
          }
          if (!leagueId) {
            toast.error('This tournament cannot be joined. League information is missing.');
            return;
          }
          setSelectedTournament({ ...tournament, leagueId });
          setDivisionModalOpen(true);
        }
      } catch (err: unknown) {
        console.error('Failed to join tournament', err);
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
        toast.error(msg || 'Failed to join tournament. Please try again.');
      }
    },
    [teamId, onSearchModalOpenChange, loadTournaments]
  );

  const handleDivisionModalClose = useCallback((open: boolean) => {
    setDivisionModalOpen(open);
    if (!open) {
      setSelectedTournament(null);
    }
  }, []);

  if (loading) {
    return (
      <div className="myteam-loading">
        <span>Loading tournaments...</span>
      </div>
    );
  }

  if (tournaments.length === 0) {
    return (
      <>
        <TeamTabEmptyState
          topActionLabel="Join New Tournament"
          topActionClassName="myteam-join-tournament-top"
          onTopAction={() => onSearchModalOpenChange(true)}
          title="No tournaments joined yet"
          subtitle="Search for tournaments and join to participate with your team."
          primaryButtonLabel="Join New Tournament"
          onPrimaryAction={() => onSearchModalOpenChange(true)}
        />
        <SearchTournamentModal
          open={searchModalOpen}
          onOpenChange={onSearchModalOpenChange}
          onSelectTournament={handleSelectTournament}
        />
        <SelectDivisionGroupModal
          open={divisionModalOpen}
          onOpenChange={handleDivisionModalClose}
          tournamentId={selectedTournament?.tournamentId ?? ''}
          teamId={teamId}
          leagueId={selectedTournament?.leagueId ?? ''}
          team={team}
          onSuccess={loadTournaments}
        />
      </>
    );
  }

  return (
    <>
      <div className="myteam-tab-with-content">
        <button
          type="button"
          className="myteam-join-tournament-top"
          onClick={() => onSearchModalOpenChange(true)}
        >
          + Join New Tournament
        </button>
        <div className="myteam-tournaments-grid">
          {tournaments.map((t) => (
            <TournamentCard
              key={t.tournamentId}
              tournament={t}
              onClick={t.teamSnapshotId ? onTournamentClick : undefined}
            />
          ))}
        </div>
      </div>
      <SearchTournamentModal
        open={searchModalOpen}
        onOpenChange={onSearchModalOpenChange}
        onSelectTournament={handleSelectTournament}
      />
      <SelectDivisionGroupModal
        open={divisionModalOpen}
        onOpenChange={handleDivisionModalClose}
        tournamentId={selectedTournament?.tournamentId ?? ''}
        teamId={teamId}
        leagueId={selectedTournament?.leagueId ?? ''}
        team={team}
        onSuccess={loadTournaments}
      />
    </>
  );
}

export default TournamentsTab;
